<script setup>
/**
 * Visão comercial — espelha o relatório que a Navi usa em Power BI: faixa de
 * KPIs, vendas por canal, funil do pipeline, churn e alerta de gargalo, nessa
 * ordem. A ordem é a do original de propósito: quem já lê aquele relatório não
 * precisa reaprender onde fica cada número.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MetricHelp from '../MetricHelp.vue';

const props = defineProps({
  report: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const { t } = useI18n();

const kpis = computed(() => props.report?.kpis || {});
const bottleneck = computed(() => props.report?.bottleneck || {});
const channels = computed(() => props.report?.sales_by_channel || []);
const series = computed(() => props.report?.leads_per_day || []);
const pipeline = computed(() => props.report?.pipeline || {});

const stages = computed(() =>
  (pipeline.value.stages || []).filter(stage => (stage.count || 0) > 0)
);

const maxStage = computed(() =>
  Math.max(...stages.value.map(stage => stage.count || 0), 1)
);

const funnelTotal = computed(() => pipeline.value.total ?? 0);

// Canal vem como identificador do banco (meta_ctwa, campaign_whatsapp). Mapa
// explícito em vez de chave dinâmica de i18n, que o lint reclama com razão.
const channelLabels = computed(() => ({
  direct: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.DIRECT'),
  campaign_whatsapp: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.CAMPAIGN_WHATSAPP'),
  meta_ctwa: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.META_CTWA'),
  form: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.FORM'),
  prospecting: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.PROSPECTING'),
  site: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.SITE'),
  scheduling: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.SCHEDULING'),
  growth_link: t('KANBAN_REPORTS.COMMERCIAL.CHANNELS.GROWTH_LINK'),
}));

const channelLabel = key => channelLabels.value[key] || key;

// Uma cor por série, como no relatório original: a cor separa o canal e a
// etapa melhor do que a mesma cor com opacidade caindo, que lia como lista.
const SERIES_COLORS = [
  'bg-n-blue-9',
  'bg-n-teal-9',
  'bg-n-amber-9',
  'bg-n-iris-9',
  'bg-n-ruby-9',
  'bg-n-slate-10',
];

const seriesColor = index => SERIES_COLORS[index % SERIES_COLORS.length];

// Funil de verdade: cada etapa é um trapézio que começa na largura dela e
// termina na largura da próxima, então as fatias se encaixam e formam o cone.
// Recorte em CSS (clip-path) em vez de SVG para o número continuar sendo texto
// normal — em SVG esticado ele sairia deformado.
const MIN_WIDTH = 16;

const funnelSlices = computed(() => {
  const larguras = stages.value.map(stage =>
    Math.max((stage.count / maxStage.value) * 100, MIN_WIDTH)
  );

  return stages.value.map((stage, i) => {
    const topo = larguras[i];
    const base = i + 1 < larguras.length ? larguras[i + 1] : topo * 0.55;
    return {
      ...stage,
      color: seriesColor(i),
      clip: `polygon(${(50 - topo / 2).toFixed(2)}% 0%, ${(50 + topo / 2).toFixed(2)}% 0%, ${(50 + base / 2).toFixed(2)}% 100%, ${(50 - base / 2).toFixed(2)}% 100%)`,
    };
  });
});

const formatRate = value => `${Number(value || 0)}%`;

const hasBottleneck = computed(
  () => Number(bottleneck.value.without_follow_up || 0) > 0
);

const maxChannel = computed(() =>
  Math.max(...channels.value.map(row => row.count || 0), 1)
);

const maxDay = computed(() =>
  Math.max(...series.value.map(point => point.count || 0), 1)
);

// Área de leads por dia, desenhada em SVG num viewBox 100x30.
const sparkPath = computed(() => {
  const pontos = series.value;
  if (pontos.length < 2) return '';
  const passo = 100 / (pontos.length - 1);
  const linha = pontos
    .map((ponto, i) => {
      const y = 30 - ((ponto.count || 0) / maxDay.value) * 26 - 2;
      return `${i === 0 ? 'M' : 'L'} ${(i * passo).toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
  return `${linha} L 100 30 L 0 30 Z`;
});

// Nome e "?" de cada indicador vêm do dicionário CRM_METRICS.
const cards = computed(() => [
  {
    key: 'leads',
    metricKey: 'LEADS',
    label: t('CRM_METRICS.LEADS.NAME'),
    value: String(kpis.value.total_leads ?? 0),
    box: 'bg-n-blue-3/40 border-n-blue-6',
    dot: 'bg-n-blue-10',
  },
  {
    key: 'sales',
    metricKey: 'SALES',
    label: t('CRM_METRICS.SALES.NAME'),
    value: String(kpis.value.total_sales ?? 0),
    box: 'bg-n-teal-3/40 border-n-teal-6',
    dot: 'bg-n-teal-10',
  },
  {
    key: 'win_rate',
    metricKey: 'WIN_RATE',
    label: t('CRM_METRICS.WIN_RATE.NAME'),
    value: formatRate(kpis.value.win_rate),
    box: 'bg-n-teal-3/40 border-n-teal-6',
    dot: 'bg-n-teal-10',
  },
  {
    key: 'lead_conversion',
    metricKey: 'LEAD_CONVERSION',
    label: t('CRM_METRICS.LEAD_CONVERSION.NAME'),
    caption: `${kpis.value.lead_conversion?.closed ?? 0}/${kpis.value.lead_conversion?.total ?? 0}`,
    value: formatRate(kpis.value.lead_conversion?.rate),
    box: 'bg-n-teal-3/40 border-n-teal-6',
    dot: 'bg-n-teal-10',
  },
]);
</script>

<template>
  <section class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
    <div class="flex items-center gap-2 mb-4">
      <span class="w-4 h-4 i-lucide-line-chart text-n-slate-10" />
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('KANBAN_REPORTS.COMMERCIAL.TITLE') }}
      </h2>
    </div>

    <p v-if="isLoading && !report" class="text-sm text-n-slate-10">
      {{ t('KANBAN_REPORTS.LOADING') }}
    </p>

    <p v-else-if="!report" class="text-sm text-n-slate-10">
      {{ t('KANBAN_REPORTS.COMMERCIAL.EMPTY') }}
    </p>

    <div v-else :class="isLoading ? 'opacity-50 transition-opacity' : ''">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div
          v-for="card in cards"
          :key="card.key"
          class="px-3 py-2.5 border rounded-lg"
          :class="card.box"
        >
          <div class="flex items-start justify-between gap-2">
            <span
              class="flex items-center gap-1 text-[11px] leading-tight text-n-slate-11"
            >
              {{ card.label }}
              <span v-if="card.caption" class="text-n-slate-10">
                ({{ card.caption }})
              </span>
              <MetricHelp :metric-key="card.metricKey" />
            </span>
            <span
              class="w-2 h-2 mt-1 rounded-full shrink-0"
              :class="card.dot"
            />
          </div>
          <div class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ card.value }}
          </div>
        </div>

        <!-- Churn e saldo dependem da fonte que a conta ainda vai escolher:
             mostrar zero seria inventar um número que ninguém calculou. -->
        <div
          data-testid="churn-card"
          class="px-3 py-2.5 border rounded-lg bg-n-ruby-3/30 border-n-ruby-8/40"
        >
          <div class="flex items-start justify-between gap-2">
            <span class="text-[11px] leading-tight text-n-slate-11">
              {{ t('KANBAN_REPORTS.COMMERCIAL.CHURN') }}
            </span>
            <span class="w-2 h-2 mt-1 rounded-full shrink-0 bg-n-ruby-9" />
          </div>
          <div class="mt-1 text-2xl font-semibold text-n-slate-10">—</div>
          <div class="text-[10px] text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.CHURN_PENDING') }}
          </div>
        </div>

        <div
          class="px-3 py-2.5 border rounded-lg bg-n-blue-3/30 border-n-blue-6"
        >
          <span class="text-[11px] leading-tight text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NET_BALANCE') }}
          </span>
          <div class="mt-1 text-2xl font-semibold text-n-slate-10">—</div>
          <div class="text-[10px] text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NET_BALANCE_HINT') }}
          </div>
        </div>
      </div>

      <div class="grid items-start grid-cols-1 gap-4 mt-4 lg:grid-cols-2">
        <div class="p-4 border rounded-lg border-n-weak bg-n-background">
          <h3 class="mb-4 text-xs font-semibold text-center text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.SALES_BY_CHANNEL') }}
          </h3>
          <p v-if="!channels.length" class="text-xs text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NO_SALES') }}
          </p>
          <div v-else class="flex items-end justify-around gap-3 h-36">
            <div
              v-for="(row, index) in channels.slice(0, 6)"
              :key="row.channel"
              data-testid="channel-row"
              class="flex flex-col items-center justify-end flex-1 h-full min-w-0"
            >
              <span
                class="mb-1 text-xs font-medium tabular-nums text-n-slate-12"
              >
                {{ row.count }}
              </span>
              <div
                class="w-full rounded-t min-h-[3px]"
                :class="seriesColor(index)"
                :style="{ height: `${(row.count / maxChannel) * 100}%` }"
              />
              <span
                class="mt-1.5 text-[10px] text-center truncate w-full text-n-slate-11"
              >
                {{ channelLabel(row.channel) }}
              </span>
            </div>
          </div>
        </div>

        <div
          data-testid="funnel"
          class="p-4 border rounded-lg border-n-weak bg-n-background"
        >
          <h3 class="text-xs font-semibold text-center text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.PIPELINE_STATUS') }}
          </h3>
          <p class="mb-4 text-xs text-center text-n-slate-10">
            {{ pipeline.funnel_name }}
            <span v-if="pipeline.funnel_name"> · </span>
            {{
              t('KANBAN_REPORTS.COMMERCIAL.PIPELINE_LEADS', {
                count: funnelTotal,
              })
            }}
          </p>
          <p v-if="!stages.length" class="text-xs text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NO_PIPELINE') }}
          </p>
          <div v-else class="flex">
            <div class="flex-1 min-w-0">
              <div
                v-for="slice in funnelSlices"
                :key="slice.name"
                data-testid="funnel-stage"
                class="flex items-center justify-center text-xs font-semibold text-white h-9"
                :class="slice.color"
                :style="{ clipPath: slice.clip }"
              >
                {{ slice.count }}
              </div>
            </div>
            <div class="w-28 shrink-0">
              <div
                v-for="slice in funnelSlices"
                :key="`rotulo-${slice.name}`"
                class="flex items-center pl-3 text-[11px] leading-tight h-9 text-n-slate-11"
              >
                {{ slice.label }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 mt-4 lg:grid-cols-2">
        <div class="p-4 border rounded-lg border-n-weak bg-n-background">
          <h3 class="mb-3 text-xs font-semibold text-center text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.CHURN_DETAIL') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.CHURN_PENDING_LONG') }}
          </p>
        </div>

        <div
          v-if="hasBottleneck"
          data-testid="bottleneck-alert"
          class="overflow-hidden border rounded-lg border-n-amber-6 bg-n-amber-2"
        >
          <h3
            class="px-4 py-2 text-xs font-semibold text-center bg-n-amber-9 text-n-slate-1"
          >
            {{ t('KANBAN_REPORTS.COMMERCIAL.BOTTLENECK_TITLE') }}
          </h3>
          <p
            class="flex items-start gap-2 px-4 pt-4 text-sm font-semibold text-n-amber-11"
          >
            <span class="w-5 h-5 mt-0.5 shrink-0 i-lucide-alert-triangle" />
            {{
              t('KANBAN_REPORTS.COMMERCIAL.BOTTLENECK', {
                count: bottleneck.without_follow_up,
                percent: bottleneck.percent,
              })
            }}
          </p>
          <p class="px-4 pb-4 text-xs text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.BOTTLENECK_HINT') }}
          </p>
        </div>

        <div
          v-else
          class="flex items-center p-4 border rounded-lg border-n-weak bg-n-background"
        >
          <p class="text-xs text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NO_BOTTLENECK') }}
          </p>
        </div>
      </div>

      <div class="p-4 mt-4 border rounded-lg border-n-weak bg-n-background">
        <div class="flex items-center gap-4">
          <h3 class="text-xs font-semibold shrink-0 text-n-slate-11">
            {{ t('KANBAN_REPORTS.COMMERCIAL.LEADS_PER_DAY') }}
          </h3>
          <svg
            v-if="sparkPath"
            class="w-full h-12"
            viewBox="0 0 100 30"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              :d="sparkPath"
              class="fill-n-blue-9/25 stroke-n-blue-9"
              stroke-width="0.7"
            />
          </svg>
          <span v-else class="text-xs text-n-slate-10">
            {{ t('KANBAN_REPORTS.COMMERCIAL.NO_SERIES') }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
